/**
 * Resolves read-only display values for multi-dimension “display” fields on forms,
 * using the parent chip dropdown’s selected option and its `cells[]` matrix.
 */

/** Walk both main and hidden subsections so parent chips are found regardless of tab. */
const SUBSECTION_KEYS = ["subsections", "hiddenSubsections"];

const walkFormElements = (formData, visit) => {
  if (!formData) return;
  for (const key of SUBSECTION_KEYS) {
    const arr = formData[key];
    if (!Array.isArray(arr)) continue;
    for (const sub of arr) {
      for (const el of sub.elements ?? []) {
        visit(el);
        if (el.type === "row-container" && el.gridElements) {
          for (const row of el.gridElements) {
            for (const cell of row ?? []) {
              if (cell) visit(cell);
            }
          }
        }
      }
    }
  }
};

export const findParentMultiDimensionChipElement = (formData, parentWorkflowVariableId) => {
  if (parentWorkflowVariableId == null || parentWorkflowVariableId === "") return null;
  const target = String(parentWorkflowVariableId);
  let found = null;
  walkFormElements(formData, (el) => {
    if (found || el.properties?.isFormDimensionDisplay) return;
    if (el.type !== "dropdown") return;
    const wid = el.properties?.workflowVariableId ?? el.smartChipId;
    if (wid != null && String(wid) === target) found = el;
  });
  return found;
};

export const resolveSelectedOptionForParent = (parentElement, rawResponse) => {
  const options = parentElement?.properties?.options ?? [];
  const hasDefaultOption = parentElement?.properties?.default ?? false; 
  if (!rawResponse && !hasDefaultOption) return null;

  if (typeof rawResponse === "object" && !Array.isArray(rawResponse)) {
    if (rawResponse.cells) return rawResponse;
    if (rawResponse.value !== undefined) {
      return (
        options.find((o) => o.value === rawResponse.value || String(o.value) === String(rawResponse.value)) ?? null
      );
    }
  }

  if (hasDefaultOption) {
    return parentElement?.properties?.default;
  }

  return options.find((o) => o.value === rawResponse || String(o.value) === String(rawResponse)) ?? null;
};

/**
 * Raw cell value for a dimension from an option's `cells` (API array shape or UI map keyed by dimensionId).
 */
export const getMultiDimensionCellRawValue = (selectedOption, dimensionId) => {
  if (selectedOption == null || dimensionId == null) return undefined;
  const { cells } = selectedOption;
  if (cells == null) return undefined;
  if (Array.isArray(cells)) {
    const cell = cells.find((c) => String(c.dimensionId) === String(dimensionId));
    return cell?.value;
  }
  return cells[dimensionId];
};

export const getMultiDimensionCellDisplayValue = (selectedOption, dimensionRowId) => {
  if (selectedOption?.cells == null || dimensionRowId == null) return "";
  const cell = selectedOption.cells.find((c) => String(c.dimensionId) === String(dimensionRowId));
  if (cell == null || cell.value === undefined || cell.value === null) return "";
  return String(cell.value);
};

export const getMultiDimensionDisplayValueForElement = (formData, formResponses, element) => {
  const parentId = element?.properties?.parentWorkflowVariableId;
  const dimRowId = element?.properties?.multiDimensionRowId;
  const parentEl = findParentMultiDimensionChipElement(formData, parentId);
  if (!parentEl) return "";
  const option = resolveSelectedOptionForParent(parentEl, formResponses[parentEl.id]);
  return getMultiDimensionCellDisplayValue(option, dimRowId);
};
